import React from 'react';
import { useTranslation } from 'react-i18next';

import { Sheet, useSheetDismiss } from '../../../../components/Sheet';
import { Button } from '../../../../components/Button';
import { Toggle } from '../../../../components/Toggle';
import { ReadingOrderList, type ReadingOrderItem } from '../ReadingOrderList';
import {
  Body,
  Group,
  GroupLabel,
  ToggleRow,
  ToggleCopy,
  ToggleTitle,
  ToggleHint,
} from './ArticleSettingsSheet.styles';

export type ArticleSettingsSheetProps = {
  pillar: boolean;
  onPillarChange: (pillar: boolean) => void;
  /** The category's articles in reading order, with the pillar first. */
  order: ReadingOrderItem[];
  onOrderChange: (order: ReadingOrderItem[]) => void;
  onClose: () => void;
};

/**
 * The two placement settings that used to sit in the middle of the article form,
 * moved behind the header's gear: whether this article is its category's "Start
 * here" guide, and where it falls in that category's reading order.
 *
 * Both are about the article's place in the cluster rather than its content, and
 * neither is touched on most edits, so they are one dialog rather than two fields
 * competing with the title for attention. They stay together because they are the
 * same decision seen twice: the pillar toggle decides which row is pinned at the
 * top of the list right below it.
 *
 * Nothing here writes to the server. The choices land in the editor's draft and
 * are committed by its Save, so closing this sheet never publishes a change on its
 * own.
 */
export const ArticleSettingsSheet = ({
  pillar,
  onPillarChange,
  order,
  onOrderChange,
  onClose,
}: ArticleSettingsSheetProps): React.ReactElement => {
  const { t } = useTranslation();
  const { closeHandle, dismiss } = useSheetDismiss(onClose);

  return (
    <Sheet
      title={t('Article settings')}
      onClose={onClose}
      closeHandle={closeHandle}
      animateResize
      footer={<Button label={t('Done')} onClick={dismiss} />}
    >
      <Body>
        <Group>
          <GroupLabel text="Placement" tag="span" />
          <ToggleRow>
            <Toggle on={pillar} label="Pillar article" onToggle={() => onPillarChange(!pillar)} />
            <ToggleCopy>
              <ToggleTitle text="Pillar article" tag="span" />
              <ToggleHint
                text='The category "Start here" guide. It opens the cluster and every other article links up to it.'
                tag="span"
              />
            </ToggleCopy>
          </ToggleRow>
        </Group>

        <Group>
          <GroupLabel text="Reading order" tag="span" />
          <ReadingOrderList items={order} onChange={onOrderChange} />
        </Group>
      </Body>
    </Sheet>
  );
};

export default ArticleSettingsSheet;
